import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/routes/shared/_api'
import { datadogLogs } from '@datadog/browser-logs'
import { getAccount } from '@/routes/dashboard/ownership/api/get-account'

const eligibleInvestmentSchema = z.object({
  eligible_shares: z.string(),
  investment_account_name: z.string(),
  investment_account_type: z.string(),
  investment_account_uuid: z.string(),
  eligible_date: z.string(),
  trade_date: z.string(),
  has_active_share_repurchase_request: z.boolean(),
  eligible_date_in_past: z.boolean(),
})

const shareRepurchaseSchema = z.object({
  count: z.number(),
  days_prior_end_of_quarter: z.number(),
  days_processed_end_of_quarter: z.number(),
  eligible_investments: z.array(eligibleInvestmentSchema),
})

type EligibleInvestment = z.infer<typeof eligibleInvestmentSchema> & {
  custodian?: { name: string } | null
}

export type ShareRepurchaseTypes = Omit<
  z.infer<typeof shareRepurchaseSchema>,
  'eligible_investments'
> & {
  eligible_investments: EligibleInvestment[]
}

const addCustodianInfo = async (
  data: z.infer<typeof shareRepurchaseSchema>
): Promise<ShareRepurchaseTypes> => {
  const investmentsWithCustodian = await Promise.all(
    data.eligible_investments.map(async (investment) => {
      if (investment.investment_account_type === 'foe_sdira') {
        const fullAccount = await getAccount(investment.investment_account_uuid)
        const custodian =
          'custodian' in fullAccount ? fullAccount.custodian : null
        return { ...investment, custodian }
      }
      return { ...investment, custodian: null }
    })
  )
  return { ...data, eligible_investments: investmentsWithCustodian }
}

export const getShareRepurchase = async (
  io_uuid: string
): Promise<ShareRepurchaseTypes> => {
  const response = await api.get(
    `/dashboard/investments/share-repurchase?io_uuid=${io_uuid}`
  )
  const parsedData = shareRepurchaseSchema.safeParse(response)

  if (!parsedData.success) {
    datadogLogs.logger.error(
      'Zod schema Error - get-investments-filters',
      parsedData.error
    )
    const unsafeUnparsedData = response as unknown as z.infer<
      typeof shareRepurchaseSchema
    >
    return addCustodianInfo(unsafeUnparsedData)
  } else {
    return addCustodianInfo(parsedData.data)
  }
}

export const useShareRepurchase = ({ io_uuid }: { io_uuid: string }) => {
  return useQuery({
    queryKey: ['share-repurchase', io_uuid],
    queryFn: () => getShareRepurchase(io_uuid),
    staleTime: Infinity,
  })
}
